import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, Badge, Button } from 'react-bootstrap';
import { UserCircle, Info, Plus, Link as LinkIcon, Ban, Mail, User, Phone, Lock } from 'lucide-react';
import {
    getGrantedAccesses,
    deleteAccess,
    createAccess
} from '../../../usecases/shared-accounts-usecase';
import {
    getCollaborators,
    createCollaborator,
    deleteCollaborator,
    updateCollaborator
} from '../../../usecases/collaborator-usecases';
import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import { roleContext } from '../../../context/role/context';
import ButtonBasic from '../../atoms/buttonBasic/view';
import ModalBtn from '../../molecules/modalButton/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import PasswordChangeModal from '../../molecules/PasswordChangeModal/view';
import './CollaboratorsManager.css';
import '../../styles/shared-redesign.css';


const COLLAB_PASSWORD_MODAL_ID = 'collab-password-change';

const CollaboratorsManager = () => {
    const { profile, suscripcion } = useContext(roleContext);
    const [collaborators, setCollaborators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Form states
    const [accountantEmail, setAccountantEmail] = useState('');
    const [collaboratorName, setCollaboratorName] = useState('');
    const [collaboratorEmail, setCollaboratorEmail] = useState('');
    const [collaboratorPassword, setCollaboratorPassword] = useState('');
    const [collaboratorPhone, setCollaboratorPhone] = useState('');
    const [accountants, setAccountants] = useState([]);
    const [selectedCollab, setSelectedCollab] = useState(null);

    // Obtener límites directamente de la suscripción (datos del backend)
    const collaboratorData = suscripcion?.collaborators || { limit: 0, usage: 0, additional_packages_limit: 0 };
    const maxUsers = collaboratorData.is_unlimited ? Infinity : (collaboratorData.limit + collaboratorData.additional_packages_limit);
    const currentUsersCount = collaboratorData.usage;

    useEffect(() => {
        fetchCollaborators();
    }, [profile?.uid, profile?.cid]);

    useEffect(() => {
        const clean = listener('confirm-revoke-collaborator', (e) => {
            const collab = e.detail.data;
            handleRevokeExecution(collab);
        });
        return () => clean();
    }, [profile?.uid, profile?.cid, collaborators, accountants]); // Dependencies to ensure execution has latest context

    const fetchCollaborators = async () => {
        if (!profile) return;
        setLoading(true);
        try {
            const [accesses, items] = await Promise.all([
                getGrantedAccesses(profile.uid, profile.cid, 1, 50),
                getCollaborators(profile.uid, profile.cid)
            ]);

            // Map collaborators to a similar structure for the table
            const mappedCollaborators = items.data.map(c => ({
                id: `collab-${c.id}`,
                real_id: c.id,
                role: c.role_id === 3 ? 'cashier' : 'collaborator',
                impersonated_user: {
                    name: c.name,
                    email: c.email,
                    url_avatar: null
                }
            }));

            // Fixed: accesses is already the flattened list of items from the usecase
            const mappedAccesses = (accesses || []).map(a => ({
                id: `access-${a.id}`,
                real_id: a.id,
                role: a.role,
                impersonated_user: {
                    name: a.granted_data?.name || a.granted_data?.email,
                    email: a.granted_data?.email,
                    url_avatar: a.granted_data?.url_avatar
                }
            }));
            setAccountants(mappedAccesses);
            setCollaborators(mappedCollaborators);
        } catch (err) {
            console.error(err);
            dispatchCustomEvent('popup', { message: '❌ Error al cargar colaboradores', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleLinkAccountant = async () => {
        if (!accountantEmail || !profile) return;

        setActionLoading(true);
        try {
            await createAccess(profile.uid, profile.cid, {
                invited_user_email: accountantEmail,
                role: 'accountant'
            });
            dispatchCustomEvent('popup', { message: '✅ Contador vinculado exitosamente', type: 'success' });
            setAccountantEmail('');
            fetchCollaborators();
            dispatchCustomEvent('close-link-accountant-modal');
        } catch (err) {
            let errorMsg = 'Error al vincular contador';
            const backendMsg = err.response?.data?.message || err.message || '';

            if (backendMsg.includes('USER_NOT_FOUND')) {
                errorMsg = 'El correo no está registrado. El contador debe crear una cuenta primero.';
            } else if (backendMsg.includes('ACCOUNTANT_LIMIT_REACHED')) {
                errorMsg = 'Has alcanzado el límite de 2 contadores vinculados.';
            } else if (backendMsg.includes('ALREADY_HAS_ACCESS')) {
                errorMsg = 'Este contador ya está vinculado a tu cuenta.';
            } else if (backendMsg.includes('CANNOT_GRANT_SELF')) {
                errorMsg = 'No puedes vincularte a ti mismo como contador.';
            }

            dispatchCustomEvent('popup', { message: `❌ ${errorMsg}`, type: 'error' });
        } finally {
            setActionLoading(false);
            dispatchCustomEvent('loading-btn-link-accountant');
        }
    };

    const handleCreateCollaborator = async () => {
        if (!collaboratorName || !collaboratorEmail || !collaboratorPassword) return;

        if (currentUsersCount >= maxUsers) {
            dispatchCustomEvent('popup', { message: '❌ Has alcanzado el límite de usuarios de tu plan.', type: 'error' });
            return;
        }

        setActionLoading(true);
        try {
            await createCollaborator(profile.uid, profile.cid, {
                name: collaboratorName,
                email: collaboratorEmail,
                password: collaboratorPassword,
                phone: collaboratorPhone,
                role_id: 3 // Default to Cashier for now
            });
            dispatchCustomEvent('popup', { message: '✅ Colaborador creado exitosamente.', type: 'success' });
            setCollaboratorName('');
            setCollaboratorEmail('');
            setCollaboratorPassword('');
            setCollaboratorPhone('');
            fetchCollaborators();
            dispatchCustomEvent('update-subscription');
            dispatchCustomEvent('close-create-collaborator-modal');
        } catch (err) {
            dispatchCustomEvent('popup', { message: `❌ ${err.message || 'Error al crear colaborador'}`, type: 'error' });
        } finally {
            setActionLoading(false);
            dispatchCustomEvent('loading-btn-create-collaborator');
        }
    };

    const handleRevokeClick = (collab) => {
        dispatchCustomEvent('notification-confirm-confirm-revoke-collaborator', collab);
    };

    const handleRevokeExecution = async (collab) => {
        setActionLoading(true);
        try {
            if (collab.role === 'cashier' || collab.role === 'collaborator') {
                await deleteCollaborator(profile.uid, profile.cid, collab.real_id);
            } else {
                await deleteAccess(profile.uid, profile.cid, collab.real_id);
            }
            dispatchCustomEvent('popup', { message: '✅ Acceso revocado', type: 'success' });
            fetchCollaborators();
            dispatchCustomEvent('update-subscription');
        } catch (err) {
            dispatchCustomEvent('popup', { message: '❌ Error al revocar acceso', type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenPasswordModal = (collab) => {
        setSelectedCollab(collab);
        dispatchCustomEvent('modal' + COLLAB_PASSWORD_MODAL_ID);
    };



    const handlePasswordUpdateSubmit = async (newPassword) => {
        await updateCollaborator(profile.uid, profile.cid, selectedCollab.real_id, {
            password: newPassword
        });
        dispatchCustomEvent('popup', { message: '✅ Contraseña actualizada exitosamente', type: 'success' });
        dispatchCustomEvent(`close-${COLLAB_PASSWORD_MODAL_ID}-modal`);
    };

    const getRoleBadge = (role) => {
        switch (role) {
            case 'accountant':
                return <Badge className="role-badge-pill bg-accountant">Contador</Badge>;
            case 'cashier':
                return <Badge className="role-badge-pill bg-cashier">Cajero</Badge>;
            default:
                return <Badge bg="secondary" className="role-badge-pill">{role}</Badge>;
        }
    };

    const combinedList = [...collaborators, ...accountants];

    return (
        <div className="collaborators-manager">

            {/* Header Actions */}
            <br></br>
            <div className="collaborators-header">
                <ModalBtn
                    id="create-collaborator"
                    type="basic"
                    title="Nuevo Colaborador"
                    icon={<Plus size={18} />}
                >
                    <div className="redesign-modal-container">
                        <div className="redesign-modal-header">
                            <h3 className="redesign-modal-title">Crear Colaborador</h3>
                            <p className="redesign-modal-desc">
                                Crea un nuevo usuario para que atienda tu punto de venta con accesos personalizados.
                            </p>
                        </div>

                        <Form className="redesign-modal-body">
                            <div className="redesign-grid">
                                <Form.Group>
                                    <Form.Label className="redesign-label">Nombre Completo</Form.Label>
                                    <div className="redesign-input-wrapper">
                                        <User className="redesign-input-icon" size={20} />
                                        <Form.Control
                                            type="text"
                                            placeholder="Ej. Juan Pérez"
                                            className="redesign-input"
                                            value={collaboratorName}
                                            onChange={(e) => setCollaboratorName(e.target.value)}
                                            required
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label className="redesign-label">Teléfono</Form.Label>
                                    <div className="redesign-input-wrapper">
                                        <Phone className="redesign-input-icon" size={20} />
                                        <Form.Control
                                            type="text"
                                            placeholder="Ej. 300 000 0000"
                                            className="redesign-input"
                                            value={collaboratorPhone}
                                            onChange={(e) => setCollaboratorPhone(e.target.value)}
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label className="redesign-label">Correo Electrónico</Form.Label>
                                    <div className="redesign-input-wrapper">
                                        <Mail className="redesign-input-icon" size={20} />
                                        <Form.Control
                                            type="email"
                                            placeholder="colaborador@tuempresa.com"
                                            className="redesign-input"
                                            value={collaboratorEmail}
                                            onChange={(e) => setCollaboratorEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label className="redesign-label">Contraseña</Form.Label>
                                    <div className="redesign-input-wrapper">
                                        <Lock className="redesign-input-icon" size={20} />
                                        <Form.Control
                                            type="password"
                                            placeholder="Mínimo 6 caracteres"
                                            className="redesign-input"
                                            value={collaboratorPassword}
                                            onChange={(e) => setCollaboratorPassword(e.target.value)}
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                </Form.Group>
                            </div>

                            <ButtonBasic
                                id="create-collaborator"
                                className="w-100 mt-2"
                                title="Crear Colaborador"
                                showLoading={true}
                                disabled={currentUsersCount >= maxUsers}
                                onClick={handleCreateCollaborator}
                            />
                        </Form>
                    </div>
                </ModalBtn>

                <ModalBtn
                    id="link-accountant"
                    type="basic"
                    title="Contador"
                    icon={<LinkIcon size={18} />}
                >
                    <div className="redesign-modal-container">
                        <div className="redesign-modal-header">
                            <h3 className="redesign-modal-title">Vincular Contador</h3>
                            <p className="redesign-modal-desc">
                                Víncula a un contador que ya tenga cuenta en la plataforma para que gestione tu información contable.
                            </p>
                        </div>

                        <Form className="redesign-modal-body">
                            <Form.Group className="mb-4">
                                <Form.Label className="redesign-label">Correo Electrónico</Form.Label>
                                <div className="redesign-input-wrapper">
                                    <Mail className="redesign-input-icon" size={20} />
                                    <Form.Control
                                        type="email"
                                        placeholder="ejemplo@contador.com"
                                        className="redesign-input"
                                        value={accountantEmail}
                                        onChange={(e) => setAccountantEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            </Form.Group>
                            <ButtonBasic
                                onClick={handleLinkAccountant}
                                id="link-accountant"
                                className="w-100"
                                showLoading={true}
                                title="Vincular Contador"
                            />
                        </Form>
                    </div>
                </ModalBtn>
            </div>

            {/* Limit Bar */}
            <div className="limit-reached-bar">
                <span>🎉 Límite alcanzado: {currentUsersCount}/{maxUsers === Infinity ? '∞' : maxUsers} {maxUsers === 1 ? 'usuario' : 'usuarios'}</span>
            </div>

            <Card className="collaborators-main-card">
                <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0">
                    <h5 className="fw-bold">Colaboradores Activos</h5>
                </Card.Header>
                <Card.Body className="px-0">
                    {loading ? (
                        <div className="text-center py-5">Cargando colaboradores...</div>
                    ) : combinedList.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <Info size={40} className="mb-2 opacity-50" />
                            <p>No tienes colaboradores vinculados actualmente.</p>
                        </div>
                    ) : (
                        <div className="collaborators-list">
                            {combinedList.map((collab) => (
                                <div key={collab.id} className="collaborator-row">
                                    <div className="collab-info">
                                        <div className="collab-avatar-big">
                                            {collab.impersonated_user?.url_avatar ? (
                                                <img src={collab.impersonated_user.url_avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <UserCircle size={28} className="text-muted" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="collab-name">{collab.impersonated_user?.name || 'Usuario'}</div>
                                            {/* <div className="collab-email">{collab.impersonated_user?.email}</div> */}
                                        </div>
                                    </div>

                                    <div className="text-center flex-grow-1">
                                        {getRoleBadge(collab.role)}
                                    </div>

                                    <div>
                                        <div className="d-flex gap-2">
                                            {collab.role !== 'accountant' && (
                                                <Button
                                                    className="btn-password-soft"
                                                    onClick={() => handleOpenPasswordModal(collab)}
                                                    disabled={actionLoading}
                                                >
                                                    <Lock size={16} /> Contraseña
                                                </Button>
                                            )}
                                            <Button
                                                className="btn-revoke-soft"
                                                onClick={() => handleRevokeClick(collab)}
                                                disabled={actionLoading}
                                            >
                                                <Ban size={16} /> Revocar
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Body>
            </Card>

            <ConfirmPopUp
                eventKey="confirm-revoke-collaborator"
                msg="¿Estás seguro de que deseas revocar el acceso a este colaborador?"
            />

            <PasswordChangeModal
                id={COLLAB_PASSWORD_MODAL_ID}
                userEmail={profile.email}
                targetName={selectedCollab?.impersonated_user?.name}
                onUpdatePassword={handlePasswordUpdateSubmit}
            />
        </div>
    );
};

export default CollaboratorsManager;

